import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useMemo, useRef, useState } from "react";
import { routes } from "@/routes/utils";
import { useDeleteHelpDocumentMutation, useGetAttachmentById, useGetHelpDocuments } from "@/services";
import {
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { helpDocAudience, ROLE_BOM } from "@/lib/utils";
import { toast } from "@/components/Toast";

const useHelpDocument = () => {
  const location = useLocation();
  const isOwner = location?.pathname?.includes("owner");
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<string>("");
  const selectedRow = useRef<any>(null);
  const navigate = useNavigate();

  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);

  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  // const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);
  
  const { data: helpDocuments, isLoading: isLoadingHelpDocuments } =
    useGetHelpDocuments();

  const { data: attachment, isLoading: isAttachmentLoading } =
    useGetAttachmentById({
      id: selectedAttachmentId,
      queryParams: {
        enabled: !!selectedAttachmentId
      }
    });

  useEffect(() => {
    if (attachment?.data) {
      window.open(attachment?.data, "_blank")
      setTimeout(() => {
        queryClient.cancelQueries({queryKey: ["attachment-by-id", selectedAttachmentId]})
      }, 100)
      setSelectedAttachmentId("");
    }
  }, [attachment])

  const { mutate: deleteInfo, isPending: isDeleteDocumentPending } =
    useDeleteHelpDocumentMutation({
      onError: (error?: any) => {
        toast.error(
          error?.response?.data?.message ||
            "Something went wrong"
        );
      },
      onSuccess: () => {
        toast.success("Help Document Deleted Successfully");
        queryClient.invalidateQueries({
          queryKey: ["help-documents"],
        });
        setOpenDeleteDialog(false);
      },
    });

  const columns = [
    {
      accessorKey: "audience",
      header: "Audience",
      accessorFn: (row: any) => String((helpDocAudience as any)[row?.audience] || ""),
    },
    {
      accessorKey: "licenseCategory",
      header: "License Category",
      accessorFn: (row: any) => String(row?.licenseCategory || ""),
    },
    {
      accessorKey: "attachment",
      header: "Doc",
      cell: ({row}: any) => {
        const file = row.original?.attachment;
        return file ? (
          <Button 
            variant="link"
            className="p-0" 
            disabled={row.original?.attachment?.attachmentId == selectedAttachmentId && isAttachmentLoading}
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedAttachmentId(row.original?.attachment?.attachmentId)
            }}
          >{file?.originalFileName}</Button>
        ) : (
          ""
        )
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      accessorFn: (row: any) =>
        row?.createdAt
          ? moment(row.createdAt).format('MM/DD/YYYY')
          : "",
    },
    ...(isOwner || !hasUpdatePermission
      ? []
      : [
          {
            id: "actions",
            header: "Actions",
            cell: ({row}: any) => {
              const data = row.original;

              return (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreVertical className="w-5 h-5 cursor-pointer" />
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    align="end"
                    className="bg-white border-0 shadow-md"
                  >
                    {/* Edit */}
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`${data.helpDocumentId}/edit`)
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        selectedRow.current = row.original;
                        setOpenDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]),
  ];

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  const onRowClick = (row: any) => {
    navigate(`${row.original?.helpDocumentId}/view`)
  }

  const onDeleteConfirm = () => {
    deleteInfo({ id: selectedRow?.current?.helpDocumentId })
  }

  return {
    onAdd,
    isMobile,
    globalFilter,
    setGlobalFilter,
    columns,
    helpDocuments,
    isLoadingHelpDocuments,
    isOwner,
    onRowClick,
    hasAddPermission,
    openDeleteDialog,
    setOpenDeleteDialog,
    selectedRow: selectedRow.current,
    isDeleteDocumentPending,
    onDeleteConfirm
  }
};

export default useHelpDocument;
